/**
 * Field Renderer
 * Automatically renders the appropriate field component based on schema type
 */

import React, { useMemo } from 'react';
import { FieldType, FieldSchema, ValidationResult } from '@/config/fieldConfig';
import { useI18n } from '@/i18n';
import TextField from './TextField';
import TextareaField from './TextareaField';
import NumberField from './NumberField';
import SelectField from './SelectField';
import CheckboxField from './CheckboxField';
import MarkdownField from './MarkdownField';
import JsonField from './JsonField';
import AgentMultiSelectField from './AgentMultiSelectField';

export interface FieldRendererProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: any;
  onBrowseTools?: () => void;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
  appContext,
  onBrowseTools,
}) => {
  const { getFieldTranslation, language } = useI18n();

  // Merge i18n translations into schema
  const translatedSchema = useMemo<FieldSchema>(() => {
    const translation = getFieldTranslation(name);
    if (!translation) {
      return schema;
    }

    // Create a new schema with i18n values merged
    const result: FieldSchema = {
      ...schema,
      label: translation.label || schema.label,
      help: translation.help || schema.help,
      placeholder: translation.placeholder || schema.placeholder,
    };

    // If translation has options, merge them with schema options
    if (translation.options && schema.options) {
      result.options = schema.options.map(opt => ({
        ...opt,
        label: translation.options?.[opt.value] || opt.label,
      }));
    }

    return result;
  }, [schema, name, getFieldTranslation, language]);

  // Check if field should be shown based on showIf condition
  if (schema.showIf && appContext) {
    const shouldShow = schema.showIf(appContext);
    if (!shouldShow) {
      return null;
    }
  }

  // Render appropriate field component based on type
  switch (translatedSchema.type) {
    case FieldType.TEXT:
      return (
        <TextField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
        />
      );

    case FieldType.TEXTAREA:
      return (
        <TextareaField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
        />
      );

    case FieldType.NUMBER:
      return (
        <NumberField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
        />
      );

    case FieldType.SELECT:
      return (
        <SelectField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
          appContext={appContext}
        />
      );

    case FieldType.CHECKBOX:
      return (
        <CheckboxField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
        />
      );

    case FieldType.MARKDOWN:
      return (
        <MarkdownField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
        />
      );

    case FieldType.JSON:
      return (
        <JsonField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={disabled}
          onBrowseTools={onBrowseTools}
        />
      );

    case FieldType.AGENT_MULTI_SELECT:
      return (
        <AgentMultiSelectField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error?.message}
          disabled={disabled}
          currentAgentType={appContext?.agent_type}
        />
      );

    default:
      console.warn(`Unknown field type: ${translatedSchema.type} for field ${name}`);
      return (
        <div className="text-red-600 text-sm">
          Error: Unknown field type "{translatedSchema.type}"
        </div>
      );
  }
};

export default FieldRenderer;
